import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Avatar
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import MainLayout from '../../components/layout/MainLayout';
import { getUserLeaderboardProfile, LeaderboardUser } from '../../services/userService';
import { getUserAchievements } from '../../services/achievementService';
import { StudentAchievement } from '../../types/achievement';
import { getAchievementIcon, getBackgroundColor, iconColors } from '../../components/achievements/AchievementUtils';

function UserAchievementProfilePage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState<LeaderboardUser | null>(null);
  const [achievements, setAchievements] = useState<StudentAchievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get search params to check for session ID
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!userId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        console.log(`Fetching profile for user ID: ${userId}${sessionId ? ` for session ${sessionId}` : ''}`);
        
        // First get the user profile, with session ID if provided
        const profileData = sessionId 
          ? await getUserLeaderboardProfile(parseInt(userId, 10), { sessionId: parseInt(sessionId) })
          : await getUserLeaderboardProfile(parseInt(userId, 10));
          
        console.log('User profile fetched successfully:', profileData);
        
        setUserProfile(profileData);
        
        // Only fetch achievements if user allows viewing them
        if (profileData.showAchievements) {
          try {
            console.log(`Fetching achievements for user ID: ${userId}`);
            const achievementsData = await getUserAchievements(parseInt(userId, 10));
            console.log(`Fetched ${achievementsData.length} achievements`);
            setAchievements(achievementsData);
          } catch (achievementErr) {
            console.error('Error fetching achievements:', achievementErr);
            // Don't fail the whole page if just achievements fail to load
            setAchievements([]);
            setError('Unable to load achievements for this user. The profile information is still available.');
          }
        } else {
          setAchievements([]);
          setError('This user has chosen to keep their achievements private.');
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
        setError('Failed to load user profile. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserProfile();
  }, [userId, sessionId]);

  // Display username or first name with last initial
  const getUserDisplayName = (user: LeaderboardUser) => {
    if (user.username) {
      return user.username;
    } else {
      return `${user.firstName} ${user.lastName.charAt(0)}.`;
    }
  };
  
  // Format date to readable format
  const formatDate = (dateString: string | Date) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Go back to leaderboard
  const handleGoBack = () => {
    navigate('/leaderboard');
  };

  return (
    <MainLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleGoBack}
          sx={{ mb: 3 }}
        >
          Back to Leaderboard
        </Button>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={60} />
          </Box>
        ) : error && !userProfile ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : userProfile && (
          <>
            {/* Profile Header */}
            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Avatar 
                  sx={{ 
                    width: 72, 
                    height: 72,
                    bgcolor: 'primary.main',
                    fontSize: '2rem'
                  }}
                >
                  {userProfile.firstName.charAt(0)}
                </Avatar>
                
                <Box>
                  <Typography variant="h4" component="h1">
                    {getUserDisplayName(userProfile)}'s Achievement Profile
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <Chip 
                      icon={<TrophyIcon />} 
                      label={`Rank #${userProfile.rank}`} 
                      color="primary" 
                    />
                    <Chip 
                      label={`${userProfile.problemsSolved} Problems Solved`} 
                      variant="outlined" 
                    />
                    <Chip 
                      label={`${userProfile.accuracy.toFixed(1)}% Accuracy`} 
                      variant="outlined" 
                    />
                  </Box>
                </Box>
              </Box>
            </Paper>
            
            {error ? (
              <Alert severity="info" sx={{ mb: 3 }}>
                {error}
              </Alert>
            ) : (
              <>
                {/* Achievement Stats */}
                <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TrophyIcon sx={{ fontSize: 24, color: 'primary.main', mr: 1.5 }} />
                    <Typography variant="h5" component="h2">
                      Achievements ({achievements.length})
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                  
                  {achievements.length === 0 ? (
                    <Alert severity="info">
                      This user has not earned any achievements yet.
                    </Alert>
                  ) : (
                    <Grid container spacing={2}>
                      {achievements.map((achievement) => (
                        <Grid item xs={12} sm={6} md={4} key={achievement.id}>
                          <Card 
                            elevation={2}
                            sx={{ 
                              height: '100%', 
                              bgcolor: getBackgroundColor(achievement.achievement?.iconName),
                              transition: 'transform 0.2s',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: 4
                              }
                            }}
                          >
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                                <Box
                                  sx={{
                                    mr: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'white',
                                    p: 1,
                                    borderRadius: '50%',
                                    boxShadow: 1
                                  }}
                                >
                                  {achievement.achievement && getAchievementIcon(achievement.achievement.iconName, iconColors[achievement.achievement.iconName as keyof typeof iconColors])}
                                </Box>
                                <Box>
                                  <Typography variant="h6" component="div" gutterBottom>
                                    {achievement.achievement?.name || 'Achievement'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Earned on {formatDate(achievement.awardedAt)}
                                  </Typography>
                                </Box>
                              </Box>
                              <Typography variant="body2">
                                {achievement.achievement?.description || 'No description available'}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Paper>
              </>
            )}
          </>
        )}
      </Container>
    </MainLayout>
  );
}

export default UserAchievementProfilePage;